import { lastValueFrom, Subject } from 'rxjs';

import { computed, Injectable, signal } from '@angular/core';
import { FundDisplay } from '@gs-2.0/shared';

import { ActionItem, FUND_ACTIONS } from '../../constants/fund/fund-actions';
import { CURRENCY_DATA } from '../../constants/fund/currency.constants';
import { ContactRoleAccessLevel } from '../../constants/fund/fund-access-levels';
import { FundTypes } from '../../constants/fund/fund-types';
import { CurrencySet } from '../../models/fund/contribute-data';
import { Fund, Transaction } from '../../models/fund/fund.model';
import { BalanceCurrency } from '../../models/fund/transaction.model';
import { User } from '../../models/fund/user.model';
import { StorageHelperService } from '../storage-helper.service';
import { TransactionApiService } from './transaction.service';
import { UserApiService } from './user.service';

export interface BalanceInfo {
  totalBalance: number;
  availableBalance: number;
  currencyCode: string;
}

/**
 * Saved fund state including balance information
 */
interface SavedFundState {
  fund: FundDisplay;
  funds: FundDisplay[];
  balance: BalanceInfo;
  userName: string;
  fundType: FundTypes;
  fundData?: Fund;
  selectedCurrency?: BalanceCurrency | null;
}

/**
 * Service for managing the currently selected/active fund in the application.
 * Uses Angular signals for reactive state management and sessionStorage for persistence.
 *
 * @example
 * ```typescript
 * constructor(private fundService: FundService) {
 *   // Load funds when component initializes
 *   this.fundService.loadFunds();
 *
 *   // Access reactive state
 *   effect(() => {
 *     const fund = this.fundService.selectedFund();
 *     console.log('Selected fund:', fund);
 *   });
 * }
 * ```
 */
@Injectable({
  providedIn: 'root',
})
export class FundService {
  private readonly SELECTED_FUND_STATE_KEY = 'gs_selected_fund_state';
  private readonly DEFAULT_CURRENCY = 'USD';
  private readonly DEFAULT_FUND_NAME = 'Unnamed Fund';

  // Private signals for internal state
  private _apiFunds = signal<Fund[]>([]);
  private _userDetails = signal<User | null>(null);
  private _cachedState = signal<SavedFundState | null>(null);
  private _balanceCurrencies = signal<BalanceCurrency[]>([]);
  private _selectedCurrency = signal<BalanceCurrency | null>(null);

  // Cached list of currencies the user can select, sourced from the
  // `currencies` field on /user/get-user-info. The list (symbol + metadata)
  // is stable across navigations, so other pages can read it without
  // re-triggering get-user-info. Balance is *not* cached here — it is
  // fetched fresh via /transaction/get-balance-currencies.
  private _availableCurrencies = signal<CurrencySet[]>([]);

  // Shared promise for in-flight loadFunds() — guarantees concurrent callers (guards,
  // ngOnInit, header bootstrap) share a single HTTP round-trip.
  private _loadFundsInFlight: Promise<void> | null = null;

  // Emits when a user explicitly switches funds via setSelectedFund(fundId, 'user') — used by
  // AnalyticsService to fire a fund_switch event. Does NOT emit for the 'system' source (the
  // one programmatic pre-selection in donor-giving.service.ts) or for the initial fund
  // selection at login (there is no "from" fund in that case).
  private _fundSwitchedSubject$ = new Subject<{ from: string; to: string }>();
  public fundSwitched$ = this._fundSwitchedSubject$.asObservable();

  // Public signals for reactive state
  selectedFundId = signal<string>('');
  isLoading = signal(false);
  isLoadingCurrencies = signal(false);
  errorMessage = signal<string | null>(null);
  parentFundExpired = signal<boolean>(false);

  // Computed signals for derived state
  funds = computed<FundDisplay[]>(() => {
    const funds = this._apiFunds();
    const selectedCurrency = this._selectedCurrency();
    const selectedId = this.selectedFundId();

    // If we have API data, use it
    if (funds.length > 0) {
      return this.flattenFunds(funds).map((display) => {
        // For the currently selected fund, use the selected currency's balance if available
        if (selectedCurrency && display.id === selectedId) {
          display.balance = selectedCurrency.balance || 0;
          display.currency = selectedCurrency.symbol;
        }
        return display;
      });
    }

    // Fall back to cached funds from session storage
    return this._cachedState()?.funds || [];
  });

  selectedFund = computed<Fund | null>(() => {
    const funds = this._apiFunds();
    const selectedId = this.selectedFundId();

    if (funds.length > 0 && selectedId) {
      return this.flattenApiFunds(funds).find((f) => this.getFundIdFromFund(f) === selectedId) || null;
    }

    return this._cachedState()?.fundData || null;
  });

  balanceInfo = computed<BalanceInfo | null>(() => {
    const funds = this._apiFunds();
    const selectedId = this.selectedFundId();

    // If we have API data, use it
    if (funds.length > 0 && selectedId) {
      const selectedFund = this.flattenApiFunds(funds).find((f) => this.getFundIdFromFund(f) === selectedId);

      if (selectedFund) {
        return this.createBalanceInfo(selectedFund);
      }
    }

    // Fall back to cached balance from session storage
    const cached = this._cachedState()?.balance;
    if (cached) {
      return cached;
    }

    return null;
  });

  userName = computed<string>(() => {
    // Use API data if available
    const details = this._userDetails();
    if (details?.firstName) {
      return details.firstName;
    }
    // Fall back to cached user name from session storage
    return this._cachedState()?.userName || '';
  });

  balanceCurrencies = computed<BalanceCurrency[]>(() => this._balanceCurrencies());

  selectedCurrency = computed<BalanceCurrency | null>(() => this._selectedCurrency());

  /**
   * Symbol list of currencies the user can select, cached from the most
   * recent /user/get-user-info response. Populated once after login and kept
   * for the session — other pages can read this without triggering another
   * get-user-info call.
   */
  availableCurrencies = computed<CurrencySet[]>(() => this._availableCurrencies());

  /**
   * Computed signal that returns the fund type of the currently selected fund.
   * Returns FundTypes.DAF as default if no fund is selected.
   */
  /**
   * Computed signal that returns whether the currently selected fund is eligible for transfers.
   * Checks fund type, access level, and expiry status.
   */
  isTransferEligible = computed<boolean>(() => {
    const fund = this.selectedFund();
    if (!fund) return false;

    const eligibleTypes = [FundTypes.DAF, FundTypes.CHARITY, FundTypes.PROJECT, FundTypes.PROJECT_CHARITY];
    if (!eligibleTypes.includes(fund.fundType as FundTypes)) return false;

    // Check access level
    const access = fund.contactRoleAccessLevel;
    const hasAccess =
      access === 'Full Access' ||
      (fund.fundType === FundTypes.PROJECT && access === 'Project Portal Transfer Access');
    if (!hasAccess) return false;

    const status = fund.charityStatus?.toLowerCase() || '';

    // DAF funds: just check not expired
    if (fund.fundType === FundTypes.DAF) {
      if (status.includes('expired')) return false;
      return true;
    }

    // Project funds: block statuses that cannot do F2F transfers
    if (fund.fundType === FundTypes.PROJECT) {
      const blockedProjectStatuses = [
        'project completed',
        'project application denied',
        'project suspended',
        'project paused',
      ];
      if (status.includes('expired')) return false;
      if (blockedProjectStatuses.includes(status)) return false;
      return true;
    }

    // Charity/ProjectCharity funds: status-based eligibility + must have projects
    if (fund.fundType === FundTypes.CHARITY || fund.fundType === FundTypes.PROJECT_CHARITY) {
      if (!fund.projects || fund.projects.length === 0) return false;

      const allowedCharityStatuses = [
        'approved - active',
        'renewal - ready to approve - not paid',
        'renewal - ready to approve - paid',
        'renewal pending - not paid',
        'renewal pending - paid',
      ];
      return allowedCharityStatuses.includes(status);
    }

    return false;
  });

  isFullAccess = computed<boolean>(() => {
    const fund = this.selectedFund();
    return fund?.contactRoleAccessLevel === ContactRoleAccessLevel.FULL_ACCESS;
  });

  isExpiredFund = computed<boolean>(() => {
    const fund = this.selectedFund();
    return !!fund?.charityStatus?.toLowerCase().includes('expired');
  });

  fundType = computed<FundTypes>(() => {
    // Use API data if available
    const fund = this.selectedFund();
    if (fund?.fundType) {
      return fund.fundType as FundTypes;
    }

    // Fall back to cached fund type from session storage
    const cached = this._cachedState()?.fundType;
    if (cached) {
      return cached;
    }

    // Default to DAF if no fund type is available
    return FundTypes.DAF;
  });

  /**
   * Computed signal that returns the balance info to display in the UI.
   * If a currency is selected via currency-management, returns that currency's balance.
   * Otherwise, returns the main fund balance.
   */
  displayedBalanceInfo = computed<BalanceInfo | null>(() => {
    const selected = this._selectedCurrency();

    // If a specific currency is selected, use its balance
    if (selected) {
      const currencyCode = this.getCurrencyCodeFromBalanceCurrency(selected);
      return {
        totalBalance: selected.balance,
        availableBalance: selected.availableAmountFundCash,
        currencyCode,
      };
    }

    // Fall back to main fund balance
    return this.balanceInfo();
  });

  constructor(
    private userApiService: UserApiService,
    private transactionApiService: TransactionApiService,
    private storageHelper: StorageHelperService
  ) {
    this.loadStateFromStorage();
  }

  /**
   * Loads funds from cache (if available) and then refreshes from API.
   * Concurrent callers share the same in-flight request — `getUserDetails`
   * and `getBalanceCurrencies` fire at most once per active load.
   *
   * @param opts.force - bypass the in-flight cache and start a fresh load
   *   (use after auth context changes, e.g. user-swap or post-mutation refresh).
   *
   * @example
   * ```typescript
   * await this.fundService.loadFunds();          // shared with concurrent callers
   * await this.fundService.loadFunds({ force: true }); // forced refetch
   * ```
   */
  loadFunds(opts: { force?: boolean } = {}): Promise<void> {
    if (!opts.force && this._loadFundsInFlight) {
      return this._loadFundsInFlight;
    }

    const promise = this.runLoadFunds().finally(() => {
      // Only clear if this is still the active in-flight promise — a
      // forced refetch may have replaced it while we were awaiting.
      if (this._loadFundsInFlight === promise) {
        this._loadFundsInFlight = null;
      }
    });
    this._loadFundsInFlight = promise;
    return promise;
  }

  private async runLoadFunds(): Promise<void> {
    this.isLoading.set(true);
    this.errorMessage.set(null);

    // Load cached state immediately for fast initial render
    this.loadStateFromStorage();

    try {
      // Fetch fresh data from API
      const details = await lastValueFrom(this.userApiService.getUserDetails());
      this._userDetails.set(details);
      if (details.currencies && details.currencies.length > 0) {
        this._availableCurrencies.set(details.currencies);
      }

      if (details.funds && details.funds.length > 0) {
        this._apiFunds.set(details.funds);
        this.handleFundSelection(details.funds);

        // Load balance currencies for the selected fund
        await this.loadBalanceCurrencies();
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      this.errorMessage.set(`Failed to load funds: ${message}`);
      throw error;
    } finally {
      this.isLoading.set(false);
    }
  }

  /**
   * Loads balance currencies for the currently selected fund.
   * Balance currencies represent different currencies held within the fund.
   *
   * @returns Promise that resolves to array of balance currencies
   *
   * @example
   * ```typescript
   * const currencies = await this.fundService.loadBalanceCurrencies();
   * console.log(`Fund has ${currencies.length} currencies`);
   * ```
   */
  async loadBalanceCurrencies(): Promise<BalanceCurrency[]> {
    const cseg5 = this.getFundId();

    // Return empty array if no fund is selected
    if (!cseg5) {
      console.warn('No fund selected. Cannot load balance currencies.');
      return [];
    }

    this.isLoadingCurrencies.set(true);

    try {
      const currencies = await lastValueFrom(
        this.transactionApiService.getBalanceCurrencies(cseg5)
      );
      this._balanceCurrencies.set(currencies);

      // Initialize selected currency to match fund's main currency
      this.initializeDefaultCurrency();

      return currencies;
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      console.error(`Failed to load balance currencies: ${message}`);
      throw error;
    } finally {
      this.isLoadingCurrencies.set(false);
    }
  }

  /**
   * Sets the currently selected fund and persists the selection to storage.
   *
   * @param fundId The unique identifier of the fund to select
   * @param source 'user' (default) for an explicit user-driven fund switch — emits on
   *   `fundSwitched$` when the selection actually changes. Pass 'system' for programmatic
   *   pre-selection (e.g. give-link auto-routing) so it is not reported as a user switch.
   *
   * @example
   * ```typescript
   * this.fundService.setSelectedFund('12345');
   * ```
   */
  setSelectedFund(fundId: string, source: 'user' | 'system' = 'user'): void {
    const funds = this.flattenApiFunds(this._apiFunds());
    const fundData = funds.find((f) => this.getFundIdFromFund(f) === fundId);

    if (!fundData) {
      console.warn(`Fund with ID ${fundId} not found`);
      return;
    }

    const previousFundId = this.selectedFundId();
    this.selectedFundId.set(fundId);

    // Reset selected currency and balance currencies when switching funds
    this._selectedCurrency.set(null);
    this._balanceCurrencies.set([]);

    // Update storage with new selection
    const fundDisplay = this.createFundDisplay(fundData);
    const balance = this.createBalanceInfo(fundData);
    this.saveStateToStorage(fundDisplay, balance);

    if (source === 'user' && previousFundId && previousFundId !== fundId) {
      const previousFundData = funds.find((f) => this.getFundIdFromFund(f) === previousFundId);
      this._fundSwitchedSubject$.next({
        from: previousFundData ? this.createFundDisplay(previousFundData).name : previousFundId,
        to: fundDisplay.name,
      });
    }

    // Reload balance currencies for the new fund
    this.loadBalanceCurrencies().catch((error) => {
      console.error('Failed to load currencies for new fund:', error);
    });
  }

  /**
   * Clears all fund state and removes data from storage.
   * Typically called on logout or session expiry.
   *
   * @example
   * ```typescript
   * this.fundService.clearFundState();
   * ```
   */
  clearFundState(): void {
    this._apiFunds.set([]);
    this._userDetails.set(null);
    this._cachedState.set(null);
    this._balanceCurrencies.set([]);
    this._selectedCurrency.set(null);
    this.selectedFundId.set('');
    this.isLoading.set(false);
    this.isLoadingCurrencies.set(false);
    this.errorMessage.set(null);
    this._loadFundsInFlight = null;
    this.storageHelper.removeItem(this.SELECTED_FUND_STATE_KEY);
  }

  /**
   * Checks if the currently selected fund matches the specified type.
   *
   * @param type The fund type to check against (from FundTypes enum)
   * @returns true if the selected fund matches the type, false otherwise
   *
   * @example
   * ```typescript
   * if (this.fundService.isFundType(FundTypes.DAF)) {
   *   console.log('Selected fund is a DAF');
   * }
   * ```
   */
  isFundType(type: FundTypes): boolean {
    const fund = this.selectedFund();
    return fund?.fundType === type;
  }

  /**
   * Checks if the user has at least one Foundation Fund (DAF) with Full Access
   * across ALL their funds, not just the currently selected one.
   * Used to determine eligibility for giving/granting.
   */
  hasAccessibleFoundationFund(): boolean {
    const allFunds = this.flattenApiFunds(this._apiFunds());
    return allFunds.some(
      fund => fund.fundType === FundTypes.DAF && fund.contactRoleAccessLevel === ContactRoleAccessLevel.FULL_ACCESS
    );
  }

  /**
   * Returns the fund ID of the first active Foundation Fund (DAF) with Full Access,
   * or null if none exists. Used to pre-select a fund when redirecting to the Grants page.
   */
  getFirstActiveFoundationFundId(): string | null {
    const allFunds = this.flattenApiFunds(this._apiFunds());
    const firstFund = allFunds.find(fund => {
      if (fund.fundType !== FundTypes.DAF) return false;
      if (fund.contactRoleAccessLevel !== ContactRoleAccessLevel.FULL_ACCESS) return false;
      const status = fund.charityStatus?.toLowerCase() || '';
      if (status.includes('expired')) return false;
      return true;
    });
    return firstFund ? this.getFundIdFromFund(firstFund) : null;
  }

  /**
   * Gets the name of the currently selected fund.
   *
   * @returns The fund name or empty string if no fund is selected
   *
   * @example
   * ```typescript
   * const name = this.fundService.getFundName();
   * console.log('Current fund:', name);
   * ```
   */
  getFundName(): string {
    const fund = this.selectedFund();
    if (!fund) return '';
    return fund.fundName || fund.preferredName || this.DEFAULT_FUND_NAME;
  }

  /**
   * Gets the unique identifier of the currently selected fund.
   *
   * @returns The fund ID or empty string if no fund is selected
   *
   * @example
   * ```typescript
   * const id = this.fundService.getFundId();
   * console.log('Current fund ID:', id);
   * ```
   */
  getFundId(): string {
    const fund = this.selectedFund();
    if (!fund) return '';
    return this.getFundIdFromFund(fund);
  }

  /**
   * Gets the balance information for the currently selected fund.
   *
   * @returns Balance info or null if no fund is selected
   *
   * @example
   * ```typescript
   * const balance = this.fundService.getFundBalance();
   * if (balance) {
   *   console.log(`Available: ${balance.availableBalance} ${balance.currencyCode}`);
   * }
   * ```
   */
  getFundBalance(): BalanceInfo | null {
    return this.balanceInfo();
  }

  /**
   * Gets the available actions for the currently selected fund based on fund type.
   *
   * Business Rules:
   * - DAF funds: contribute, grant, activity, transfer, currencyConversion
   * - CHARITY/PROJECT funds: contribute, distribute, activity, currencyConversion
   * - Default (no fund selected): DAF actions
   *
   * @returns Array of available fund actions
   *
   * @example
   * ```typescript
   * const actions = this.fundService.getFundActions();
   * actions.forEach(action => console.log(action.titleKey));
   * ```
   */
  getFundActions(): ActionItem[] {
    const transferEligible = this.isTransferEligible();
    const isFullAccess = this.isFullAccess();
    const isExpired = this.isExpiredFund();

    // Prioritize cached fund type for instant rendering
    const cachedState = this._cachedState();
    if (cachedState?.fundType) {
      return this.getActionsForFundType(cachedState.fundType, transferEligible, isFullAccess, isExpired);
    }

    // Fall back to API fund data if no cache
    const fund = this.selectedFund();
    if (fund?.fundType) {
      return this.getActionsForFundType(fund.fundType as FundTypes, transferEligible, isFullAccess, isExpired);
    }

    // Default to DAF actions when no fund data available
    return this.getActionsForFundType(FundTypes.DAF, transferEligible, isFullAccess, isExpired);
  }

  /**
   * Returns actions array based on fund type
   * @param fundType The type of fund
   * @param transferEligible Whether the selected fund is eligible for transfers
   * @returns Array of ActionItems appropriate for the fund type
   */
  private getActionsForFundType(fundType: FundTypes | string, transferEligible = true, isFullAccess = true, isExpired = false): ActionItem[] {
    if (isExpired) {
      return [FUND_ACTIONS.ACTIVITY];
    }

    const actions: ActionItem[] = [];

    switch (fundType) {
      case FundTypes.CHARITY:
        actions.push(FUND_ACTIONS.CONTRIBUTE);
        if (isFullAccess) actions.push(FUND_ACTIONS.DISTRIBUTE);
        actions.push(FUND_ACTIONS.ACTIVITY);
        if (transferEligible) actions.push(FUND_ACTIONS.TRANSFER);
        if (isFullAccess) actions.push(FUND_ACTIONS.CURRENCY_CONVERSION);
        break;
      case FundTypes.PROJECT:
      case FundTypes.PROJECT_CHARITY:
        actions.push(FUND_ACTIONS.CONTRIBUTE, FUND_ACTIONS.ACTIVITY);
        if (transferEligible) actions.push(FUND_ACTIONS.TRANSFER);
        break;

      case FundTypes.DAF:
      default:
        actions.push(FUND_ACTIONS.CONTRIBUTE);
        if (isFullAccess) actions.push(FUND_ACTIONS.GRANT);
        actions.push(FUND_ACTIONS.ACTIVITY);
        if (transferEligible) actions.push(FUND_ACTIONS.TRANSFER);
        if (isFullAccess) actions.push(FUND_ACTIONS.CURRENCY_CONVERSION);
        break;
    }

    return actions;
  }

  /**
   * Gets the transactions for the currently selected fund.
   *
   * TODO: Implement proper API integration to fetch transactions
   *
   * @returns Array of transactions (placeholder - returns empty array)
   *
   * @example
   * ```typescript
   * const transactions = this.fundService.getFundTransactions();
   * console.log(`Found ${transactions.length} transactions`);
   * ```
   */
  getFundTransactions(): Transaction[] {
    // Placeholder implementation
    // TODO: Implement API call to fetch transactions for the selected fund
    return [];
  }

  /**
   * Sets the selected currency for display in the UI.
   * This affects the displayedBalanceInfo computed signal.
   * Also persists the selection to session storage.
   *
   * @param currency The balance currency to select, or null to use the main fund balance
   *
   * @example
   * ```typescript
   * const eurCurrency = currencies.find(c => c.id === 'EUR');
   * this.fundService.setSelectedCurrency(eurCurrency);
   * ```
   */
  setSelectedCurrency(currency: BalanceCurrency | null): void {
    this._selectedCurrency.set(currency);

    // Save the updated state to session storage
    const selectedFund = this.selectedFund();
    const balance = this.balanceInfo();
    if (selectedFund && balance) {
      const fundDisplay = this.createFundDisplay(selectedFund);
      this.saveStateToStorage(fundDisplay, balance);
    }
  }

  /**
   * Initializes the selected currency to match the fund's main currency.
   * Called automatically after loading balance currencies or switching funds.
   * Only sets default if no currency is already selected (e.g., from cached state).
   */
  initializeDefaultCurrency(): void {
    const currencies = this._balanceCurrencies();
    const fundBalance = this.balanceInfo();
    const currentSelected = this._selectedCurrency();

    // If a currency is already selected (e.g., from cache), refresh it with fresh API data
    if (currentSelected) {
      // Verify the cached currency exists in the loaded currencies
      const freshCurrency = currencies.find(
        (c) => c.id === currentSelected.id && c.symbol === currentSelected.symbol
      );

      // Replace the cached object with fresh API data to pick up updated balances
      if (freshCurrency) {
        this._selectedCurrency.set(freshCurrency);
        const selectedFund = this.selectedFund();
        const balance = this.balanceInfo();
        if (selectedFund && balance) {
          const fundDisplay = this.createFundDisplay(selectedFund);
          this.saveStateToStorage(fundDisplay, balance);
        }
        return;
      }
    }

    if (!fundBalance || currencies.length === 0) {
      this._selectedCurrency.set(null);
      return;
    }

    // Find the currency that matches the fund's main currency
    // Compare both symbol and id to handle different fund data formats
    const mainCurrency = currencies.find(
      (c) => c.symbol === fundBalance.currencyCode || c.id === fundBalance.currencyCode
    );

    if (mainCurrency) {
      this._selectedCurrency.set(mainCurrency);
    } else {
      // If main currency not found in balance currencies, use first available
      this._selectedCurrency.set(currencies[0] || null);
    }
  }

  // Private helper methods

  /**
   * Extracts a valid currency code from a BalanceCurrency object.
   * The BalanceCurrency.id might contain a numeric ID instead of ISO currency code,
   * so we need to validate it and find the proper code.
   *
   * @param currency The balance currency object
   * @returns A valid ISO currency code string
   */
  private getCurrencyCodeFromBalanceCurrency(currency: BalanceCurrency): string {
    // Check if the symbol is a valid ISO currency code (exists in CURRENCY_DATA)
    // The symbol field from API usually contains the ISO code like "AUD", "USD", etc.
    if (CURRENCY_DATA[currency.symbol as keyof typeof CURRENCY_DATA]) {
      return currency.symbol;
    }

    // Check if the ID is a valid currency code (exists in CURRENCY_DATA)
    if (CURRENCY_DATA[currency.id as keyof typeof CURRENCY_DATA]) {
      return currency.id;
    }

    // Last fallback: use the fund's main currency code
    const mainBalance = this.balanceInfo();
    return mainBalance?.currencyCode || this.DEFAULT_CURRENCY;
  }

  /**
   * Handles fund selection logic based on cached state
   */
  private handleFundSelection(funds: Fund[]): void {
    const cachedState = this._cachedState();

    if (cachedState) {
      // Verify that the cached fund still exists (including nested projects)
      const allFunds = this.flattenApiFunds(funds);
      const fundData = allFunds.find((f) => this.getFundIdFromFund(f) === cachedState.fund.id);

      if (fundData) {
        // Keep the cached selection
        // Update sessionStorage with fresh balance data from API
        const updatedBalance = this.createBalanceInfo(fundData);
        const updatedFundDisplay = this.createFundDisplay(fundData);
        this.saveStateToStorage(updatedFundDisplay, updatedBalance);
      } else {
        // Cached fund no longer exists, select the first fund
        this.selectDefaultFund(funds);
      }
    } else {
      // No cached state, select the first fund
      this.selectDefaultFund(funds);
    }
  }

  /**
   * Selects and saves the first fund as the default
   */
  private selectDefaultFund(funds: Fund[]): void {
    if (funds.length === 0) return;

    const firstFund = funds[0];
    const fundId = this.getFundIdFromFund(firstFund);
    this.selectedFundId.set(fundId);

    const fundDisplay = this.createFundDisplay(firstFund);
    const balance = this.createBalanceInfo(firstFund);

    this.saveStateToStorage(fundDisplay, balance);
  }

  /**
   * Recursively flattens a fund hierarchy into a flat array with level and parentId set
   */
  private flattenApiFunds(funds: Fund[]): Fund[] {
    const result: Fund[] = [];
    for (const fund of funds) {
      result.push(fund);
      if (fund.projects?.length) {
        result.push(...this.flattenApiFunds(fund.projects));
      }
    }
    return result;
  }

  private flattenFunds(funds: Fund[], level = 0, parentId?: string): FundDisplay[] {
    const result: FundDisplay[] = [];
    for (const fund of funds) {
      const fundId = this.getFundIdFromFund(fund);
      const display = this.createFundDisplay(fund);
      display.level = level;
      display.parentId = parentId;
      result.push(display);
      if (fund.projects && fund.projects.length > 0) {
        result.push(...this.flattenFunds(fund.projects, level + 1, fundId));
      }
    }
    return result;
  }

  /**
   * Gets the unique identifier for a fund
   */
  private getFundIdFromFund(fund: Fund): string {
    return fund.cseg5 || fund.fundNumber;
  }

  /**
   * Creates balance information from fund data
   */
  private createBalanceInfo(fund: Fund): BalanceInfo {
    return {
      totalBalance: fund.closingBalance || 0,
      availableBalance: fund.availableAmount || 0,
      currencyCode: fund.currencySymbol || fund.currency || this.DEFAULT_CURRENCY,
    };
  }

  /**
   * Creates a fund display object from fund data
   */
  private createFundDisplay(fund: Fund): FundDisplay {
    return {
      id: this.getFundIdFromFund(fund),
      name: fund.fundName || fund.preferredName || this.DEFAULT_FUND_NAME,
      accountNumber: fund.fundNumber,
      balance: fund.closingBalance || 0,
      currency: fund.currencySymbol || fund.currency || this.DEFAULT_CURRENCY,
      data: fund,
    };
  }

  /**
   * Creates a fund display object from fund data with a specific currency balance
   */
  private createFundDisplayWithCurrency(fund: Fund, currency: BalanceCurrency): FundDisplay {
    return {
      id: this.getFundIdFromFund(fund),
      name: fund.fundName || fund.preferredName || this.DEFAULT_FUND_NAME,
      accountNumber: fund.fundNumber,
      balance: currency.balance || 0,
      currency: currency.symbol,
      data: fund,
    };
  }

  /**
   * Saves the complete fund state to session storage
   */
  private saveStateToStorage(fund: FundDisplay, balance: BalanceInfo): void {
    const userDetails = this._userDetails();
    const allFunds = this.funds();

    // Create clean copies without the full data property to avoid serialization issues
    const cleanFund: FundDisplay = {
      id: fund.id,
      name: fund.name,
      accountNumber: fund.accountNumber,
      balance: fund.balance,
      currency: fund.currency,
    };

    const cleanFunds: FundDisplay[] = allFunds.map((f) => ({
      id: f.id,
      name: f.name,
      accountNumber: f.accountNumber,
      balance: f.balance,
      currency: f.currency,
      level: f.level,
      parentId: f.parentId,
    }));

    const state: SavedFundState = {
      fund: cleanFund,
      funds: cleanFunds,
      balance,
      userName: userDetails?.firstName || '',
      fundType: fund.data?.fundType || FundTypes.DAF,
      fundData: fund.data,
      selectedCurrency: this._selectedCurrency(),
    };

    this.storageHelper.setItem(this.SELECTED_FUND_STATE_KEY, state);
    this._cachedState.set(state);
  }

  /**
   * Loads state from session storage
   */
  private loadStateFromStorage(): void {
    const savedState = this.storageHelper.getItem<SavedFundState>(this.SELECTED_FUND_STATE_KEY);

    if (savedState) {
      this._cachedState.set(savedState);
      this.selectedFundId.set(savedState.fund.id);
      // Restore selected currency if it was saved
      if (savedState.selectedCurrency) {
        this._selectedCurrency.set(savedState.selectedCurrency);
      }
      // Don't set loading to false here - let it remain true until API calls complete
      // This ensures the loading overlay stays visible during data fetching
    }
  }
}
